import React, { useCallback } from "react";
import type { BankAccount } from "@meupedaco/shared";
import { CrudPage } from "../../components/CrudPage";
import { useAuth } from "../../auth/AuthContext";
import { TIPOS_CONTA_BANCARIA } from "../../lib/options";
import { formatMoneyBR, parseMoneyBR } from "../../utils/format";

export const BankAccountsPage: React.FC = () => {
  const { client } = useAuth();

  const fetchBanks = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/banks?q=${encodeURIComponent(query)}&take=10&skip=0`
      );
      if (!response.ok) {
        return [];
      }
      const payload = (await response.json()) as { data: Array<{ id: number; name: string; code?: string | null }> };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.name}${item.code ? ` (${item.code})` : ""}`,
      }));
    },
    [client]
  );

  const fetchBankAccounts = useCallback(
    async (query: string) => {
      const response = await client.get(
        `/modules/bank-accounts?q=${encodeURIComponent(query)}&take=20&skip=0`
      );
      if (!response.ok) return [];
      const payload = (await response.json()) as {
        data: Array<{ id: number; agency: string; accountNumber: string; bank: { name: string } }>;
      };
      return payload.data.map((item) => ({
        id: item.id,
        label: `${item.bank.name} ag ${item.agency} / cc ${item.accountNumber}`,
      }));
    },
    [client]
  );

  return (
    <CrudPage<BankAccount>
      title="Contas bancarias"
      endpoint="/modules/bank-accounts"
      searchPlaceholder="Buscar por agencia, conta, banco..."
      columns={[
        { label: "Banco", render: (item) => item.bank.name },
        { label: "Agencia", render: (item) => item.agency },
        { label: "Conta", render: (item) => item.accountNumber },
        { label: "Tipo", render: (item) => item.accountType || "-" },
        {
          label: "Saldo inicial",
          render: (item) => formatMoneyBR(item.initialBalance ?? 0),
        },
        {
          label: "Ct. Remuneracao",
          render: (item) =>
            (item as BankAccount & { remunerationAccount?: { agency: string; accountNumber: string; bank: { name: string } } | null }).remunerationAccount ? (
              <span className="badge bg-info text-dark" style={{ fontSize: "0.7rem" }}>
                {(item as BankAccount & { remunerationAccount?: { agency: string; accountNumber: string; bank: { name: string } } | null }).remunerationAccount!.bank.name}{" "}
                {(item as BankAccount & { remunerationAccount?: { agency: string; accountNumber: string; bank: { name: string } } | null }).remunerationAccount!.accountNumber}
              </span>
            ) : "-",
        },
        {
          label: "Padrao",
          render: (item) =>
            item.isDefaultForPayments ? (
              <span className="badge bg-success">Padrao</span>
            ) : (
              "-"
            ),
        },
      ]}
      formFields={[
        {
          name: "bankId",
          label: "Banco",
          type: "autocomplete",
          placeholder: "Buscar banco",
          fetchOptions: fetchBanks,
          getIdFromItem: (item) => item.bankId,
          getLabelFromItem: (item) => item.bank.name,
        },
        { name: "agency", label: "Agencia", type: "text", required: true },
        {
          name: "accountNumber",
          label: "Numero da conta",
          type: "text",
          required: true,
        },
        {
          name: "accountType",
          label: "Tipo da conta",
          type: "select",
          options: TIPOS_CONTA_BANCARIA,
        },
        { name: "initialBalance", label: "Saldo inicial", type: "text" },
        {
          name: "remunerationAccountId",
          label: "Conta remuneração (BTG)",
          type: "autocomplete",
          placeholder: "Buscar conta remuneração",
          fetchOptions: fetchBankAccounts,
          getIdFromItem: (item) =>
            (item as BankAccount & { remunerationAccountId?: number | null }).remunerationAccountId ?? null,
          getLabelFromItem: (item) => {
            const r = (item as BankAccount & { remunerationAccount?: { agency: string; accountNumber: string; bank: { name: string } } | null }).remunerationAccount;
            return r ? `${r.bank.name} ag ${r.agency} / cc ${r.accountNumber}` : "";
          },
        },
        {
          name: "isDefaultForPayments",
          label: "Conta padrao para pagamentos",
          type: "checkbox",
        },
        { name: "isActive", label: "Ativo", type: "checkbox" },
      ]}
      initialValues={{
        bankId: null,
        agency: "",
        accountNumber: "",
        accountType: "",
        initialBalance: "0",
        remunerationAccountId: null,
        isDefaultForPayments: false,
        isActive: true,
      }}
      mapToPayload={(values) => ({
        bankId: Number(values.bankId),
        agency: values.agency,
        accountNumber: values.accountNumber,
        accountType: values.accountType || null,
        initialBalance: parseMoneyBR(String(values.initialBalance ?? "0")),
        remunerationAccountId: values.remunerationAccountId ? Number(values.remunerationAccountId) : null,
        isDefaultForPayments: Boolean(values.isDefaultForPayments),
        isActive: values.isActive,
      })}
      permissionWrite="BANK_ACCOUNT_WRITE"
    />
  );
};
